import React, { useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Button,
    ThemeProvider,
    createTheme,
    CssBaseline,
    useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import logo from "./images/hnh-logo.png";
import thumb from "./images/thumb.jpg";

// Assets
const videoEmbed = "https://www.youtube.com/embed/zeOtz5MSYrM";
const videoThumbnail = "https://img.youtube.com/vi/zeOtz5MSYrM/0.jpg";
const languagesImg = "./images/king.png";

// Theme
const theme = createTheme({
    typography: {
        fontFamily: ["Kurale", "serif"].join(","),
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: `
                @media (min-width: 900px) {
                    body {
                        overflow: hidden;
                    }
                }
                @media (max-width: 899px) {
                    body {
                        overflow-x: hidden;
                    }
                }
            `,
        },
    },
});

// Reusable Styles
const styles = {
    mainContainer: {
        minHeight: "100vh",
        width: "100%",
        overflowY: 'auto',  // Dikey kaydırmaya izin ver (mobilde çalışacak)
        overflowX: 'hidden', // Yatay kaydırmayı kapat (tüm cihazlarda)
        background: "radial-gradient(circle, #f0543f 5%, #15182b 100%)",
        py: 1,
        position: "relative",
    },
    backButton: {
        position: "absolute",
        top: 16,
        left: 16,
        zIndex: 1200,
        background: "#222c37",
        color: "#ffcc33",
        fontWeight: "bold",
        borderRadius: 2,
        px: 2,
        py: 1,
        boxShadow: 2,
        "&:hover": { background: "#15182b" },
    },
    contentContainer: {
        maxWidth: { xs: "95%", md: "1400px" },
        margin: "0 auto",
        padding: { xs: 1, md: 2 },
    },
    mediaContainer: {
        width: { xs: "100%", sm: "100%", md: "854px" },
        maxWidth: "100%",
        borderRadius: 2,
        overflow: "hidden",
        mb: 2,
        mt: { xs: 6, md: 0 }, // Mobilde videonun üstünde boşluk
        background: "#222c37",
        boxShadow: 3,
    },
    thumbnailContainer: {
        display: "flex",
        gap: 2,
        mb: 2,
        width: { xs: "100%", sm: "100%", md: "854px" },
        maxWidth: "100%",
        justifyContent: "center",
        flexWrap: { xs: "wrap", md: "nowrap" },
    },
    thumbnail: {
        width: 96,
        height: 54,
        objectFit: "cover",
        borderRadius: 1,
        cursor: "pointer",
        border: "2px solid #fff",
    },
    infoBox: {
        background: "#222c37",
        borderRadius: 2,
        p: { xs: 2, md: 4 },
        color: "#f3ecd8",
        boxShadow: 3,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
    },
    primaryButton: {
        background: "linear-gradient(90deg, #ffb347 0%, #ffcc33 100%)",
        color: "#15182b",
        fontWeight: "bold",
        fontSize: { xs: "0.9rem", md: "1.1rem" },
        borderRadius: 2,
        boxShadow: 2,
        "&:hover": {
            background: "linear-gradient(90deg, #ffcc33 0%, #ffb347 100%)",
        },
    },
    tag: {
        px: 1.5,
        py: 0.5,
        background: "#181f27",
        color: "#ffcc33",
        borderRadius: 1,
        fontSize: "0.95rem",
        fontWeight: 500,
        border: "1px solid #ffcc33",
        boxShadow: 1,
        letterSpacing: 0.2,
        userSelect: "none",
    },
};

// Component Parts
const MediaViewer = ({ type, src }) => {
    return type === "video" ? (
        <Box sx={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
            <iframe
                src={src}
                title="Game Trailer"
                frameBorder="0"
                allowFullScreen
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                }}
            />
        </Box>
    ) : (
        <Box
            component="img"
            src={src}
            alt="Screenshot"
            sx={{ width: "100%", display: "block" }}
        />
    );
};

const ThumbnailGallery = ({ onThumbnailClick }) => {
    return (
        <Box sx={styles.thumbnailContainer}>
            <Box
                component="img"
                src={videoThumbnail}
                alt="Trailer"
                onClick={() => onThumbnailClick("video", videoEmbed)}
                sx={styles.thumbnail}
            />
            {[1, 2, 3].map((i) => (
                <Box
                    key={i}
                    component="img"
                    src={thumb}
                    alt={`Screenshot ${i}`}
                    onClick={() => onThumbnailClick("image", thumb)}
                    sx={styles.thumbnail}
                />
            ))}
        </Box>
    );
};

const GameDescription = () => {
    return (
        <Box sx={{ ...styles.infoBox, mt: 2 }}>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: "bold" }}>
                About This Game
            </Typography>
            <Typography variant="body1">
                Hide 'n Heist is a multiplayer party game where you and your friends
                disguise as medieval props, steal treasures, and outsmart the guardian!
                Whether you're a mischievous thief or a vigilant protector, chaos is guaranteed.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4}}>
                {["Images", "Logo", "Videos"].map((item) => (
                    <Button
                        key={item}
                        variant="contained"
                        sx={styles.primaryButton}
                    >
                        Download {item}
                    </Button>
                ))}
            </Box>
        </Box>
    );
};

const GameInfoSection = () => {
    const gameInfo = [
        { label: "Release Date", value: "Coming Soon", highlight: true },
        { label: "Developer", value: "Doin' Together Games" },
        { label: "Publisher", value: "Doin' Together Games" },
    ];

    const tags = ["Multiplayer", "Party Game", "Stealth", "Action", "Comedy"];

    return (
        <Box sx={styles.infoBox}>
            <Box
                component="img"
                src={thumb}
                alt="Game Logo"
                sx={{
                    width: "60%",
                    mb: 2,
                    borderRadius: 3,
                    alignSelf: "center"
                }}
            />

            <Typography variant="h4" sx={{
                fontWeight: "bold",
                mb: 2,
                textAlign: "center"
            }}>
                Hide 'n Heist
            </Typography>

            <Typography variant="body1" sx={{
                mb: 3,
                textAlign: "center"
            }}>
                Hide 'n Heist is a chaotic and hilarious multiplayer game where players
                become medieval props to pull off heists under the nose of an ever-watchful guardian.
                Team up, blend in, and loot the castle!
            </Typography>

            <Box sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3
            }}>
                <Button
                    variant="contained"
                    sx={{
                        ...styles.primaryButton,
                        px: 4,
                    }}
                    onClick={() => window.open("https://store.steampowered.com/app/3321300/Hide_n_Heist", "_blank")}
                >
                    Play Now
                </Button>
            </Box>

            <Box sx={{ mb: 2, width: "100%" }}>
                {gameInfo.map((info) => (
                    <Typography key={info.label} sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1
                    }}>
                        <strong>{info.label}:</strong>
                        <Box component="span" sx={{
                            ml: 1,
                            flex: 1,
                            display: "flex",
                            justifyContent: "flex-end"
                        }}>
                            {info.highlight ? (
                                <span style={{ color: "#ffcc33" }}>{info.value}</span>
                            ) : info.value}
                        </Box>
                    </Typography>
                ))}
            </Box>

            <Box sx={{ width: "100%"}}>
                <Typography variant="h5" sx={{ mb: 1 }}>
                    Contact
                </Typography>
                <Typography>
                    <EmailIcon sx={{
                        verticalAlign: "middle",
                        mr: 1,
                        color: "#ffcc33"
                    }} />
                    Email:{" "}
                    <a
                        href="mailto:dointogethergames@gmail.com"
                        style={{
                            color: "#ffcc33",
                            fontWeight: "bold",
                            textDecoration: "underline dotted"
                        }}
                    >
                        dointogethergames@gmail.com
                    </a>
                </Typography>

                <Box sx={{ mt: 3, mb: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Popular user-defined tags:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {tags.map((tag) => (
                            <Box key={tag} sx={styles.tag}>
                                <Typography>{tag}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Supported Languages
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {["🇬🇧", "🇹🇷", "🇹🇷", "🇹🇷", "🇹🇷", "🇹🇷", "🇹🇷", "🇹🇷", "🇹🇷", "🇹🇷", "🇹🇷"].map((flag, i) => (
                            <span key={i} role="img" aria-label={`Flag ${i}`}>{flag}</span>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// Main Component
const PressKit = () => {
    const [mainContent, setMainContent] = useState({
        type: "video",
        src: videoEmbed,
    });

    // Mobil cihaz kontrolü için
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleThumbnailClick = (type, src) => {
        setMainContent({ type, src });
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={styles.mainContainer}>
                <Button
                    onClick={() => window.history.back()}
                    startIcon={<ArrowBackIcon />}
                    sx={styles.backButton}
                >
                    Back
                </Button>

                <Grid container spacing={2} justifyContent="center" sx={styles.contentContainer}>
                    {/* Media Section */}
                    <Grid item xs={12} md={7}>
                        <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            maxWidth: "100%",
                        }}>
                            <Box sx={styles.mediaContainer}>
                                <MediaViewer type={mainContent.type} src={mainContent.src} />
                            </Box>

                            <ThumbnailGallery onThumbnailClick={handleThumbnailClick} />

                            <Box sx={{ width: "100%" }}>
                                <GameDescription />
                            </Box>
                        </Box>
                    </Grid>

                    {/* Info Section */}
                    <Grid item xs={12} md={5}>
                        <GameInfoSection />
                    </Grid>
                </Grid>
            </Box>
        </ThemeProvider>
    );
};

export default PressKit;
